<template>
    <div>
        <div class="plain-card">
            <h5>
                <span @click.prevent="toggleFilters($event)" class="pointer">
                    <i class="fa fa-caret-down pull-left active pen-pink"></i>
                    {{ show_filters ? uLang('app.hide_filters') : uLang('app.show_filters') }}
                </span>
            </h5>

            <template v-if="show_filters">
                <!-- Date filter presets -->
                <div class="row form-group">
                    <div class="col-md-6 col-sm-12">
                        <label class="col-md-2 col-sm-12 col-form-label">{{ uLang('app.period') }}</label>
                        <div class="col-md-9 col-xl-10 col-sm-12">
                            <b-button-group>
                                <b-button
                                    v-for="(preset, key) in date_filter_presets"
                                    @click="applyDatePreset(preset.months, key)"
                                    :key="key"
                                    :class="key === selected_date_preset ? 'pressed' : ''"
                                    v-text="preset.name"
                                    class="datePresetButton"
                                    variant="brand"
                                />
                            </b-button-group>
                        </div>
                    </div>

                    <div class="col-md-6 col-sm-12">
                        <label class="col-md-2 col-sm-12 col-form-label">{{ uLang('app.collaboration') }}</label>
                        <div class="col-md-9 col-xl-10 col-sm-12">
                            <b-button-group>
                                <b-button
                                    class="datePresetButton"
                                    variant="brand"
                                    @click="filterCollaborationStatus('active')"
                                    :class="filters.collaboration_statuses.active ? 'pressed' : ''"
                                    v-text="uLang('app.collaboration_active')"
                                />

                                <b-button
                                    class="datePresetButton"
                                    variant="brand"
                                    @click="filterCollaborationStatus('ended')"
                                    :class="filters.collaboration_statuses.ended ? 'pressed' : ''"
                                    v-text="uLang('app.collaboration_ended')"
                                />
                            </b-button-group>
                        </div>
                    </div>
                </div>

                <!-- Date filter custom inputs -->
                <div v-if="selected_date_preset === 'custom'" class="row form-group mb-3">
                    <div class="col-md-6 col-sm-12">
                        <label class="col-md-2 col-sm-12 col-form-label">{{ uLang('app.date_from_filter') }}</label>
                        <div class="col-md-9 col-xl-10 col-sm-12">
                            <div class="input-group">
                                <vue-pikaday
                                    v-model="filters.date_from"
                                    placeholder="Van"
                                    class="form-control"
                                    autocomplete="off"
                                    @input="updateData()"
                                />
                                <button
                                    v-if="filters.date_from !== null"
                                    @click="clearFilter('date_from')"
                                    class="btn btn-brand input-group-append"
                                >
                                    <i class="fa fa-times"></i>
                                </button>
                            </div>
                        </div>
                    </div>

                    <div class="col-md-6 col-sm-12">
                        <label class="col-md-2 col-sm-12 col-form-label">{{ uLang('app.date_to_filter') }}</label>
                        <div class="col-md-9 col-xl-10 col-sm-12">
                            <div class="input-group">
                                <vue-pikaday
                                    v-model="filters.date_to"
                                    placeholder="Tot"
                                    class="form-control"
                                    autocomplete="off"
                                    @input="updateData()"
                                />
                                <button
                                    v-if="filters.date_to !== null"
                                    @click="clearFilter('date_to')"
                                    class="btn btn-brand input-group-append"
                                >
                                    <i class="fa fa-times"></i>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Trajectory types and projects -->
                <div class="row form-group">
                    <div class="col-md-6 col-sm-12">
                        <label class="col-md-2 col-sm-12 col-form-label">{{ uLang('app.projects') }}</label>
                        <div class="col-md-9 col-xl-10 col-sm-12">
                            <multi-select
                                v-model="selectedProjects"
                                :options="availableFilterOptions.projects"
                                :placeholder="uLang('app.projects')"
                                :close-on-select="false"
                                :show-labels="false"
                                @input="updateData()"
                                :limit="2"
                                :limit-text="limitText('projects')"
                                id="projects"
                                name="projects"
                                track-by="id"
                                label="name"
                                return="id"
                                multiple
                            >
                                <template slot="tag" slot-scope="props">
                                    <span class="multiselect__tag" :title="props.option && props.option.name">
                                        <span>{{ props.option && props.option.name }}</span>
                                        <i class="multiselect__tag-icon" aria-hidden="true" @mousedown.prevent="props.remove(props.option)"></i>
                                    </span>
                                </template>
                                <template slot="beforeList">
                                    <li class="multiselect__element multiselect__element__idplein" @click.prevent.stop="handleCustomOptionProject()"><span class="multiselect__option"><span>Selecteer alles</span></span></li>
                                </template>
                                <template slot="clear" slot-scope="props">
                                    <div v-show="filters.projects.length"
                                         @click="clearFilter('projects', [])"
                                         class="multiselect__clear"
                                    ></div>
                                </template>
                            </multi-select>
                        </div>
                    </div>

                    <div class="col-md-6 col-sm-12">
                        <label class="col-md-2 col-sm-12 col-form-label">{{ uLang('app.trajectory_types') }}</label>
                        <div class="col-md-9 col-xl-10 col-sm-12">
                            <multi-select
                                v-model="selectedTrajectoryTypes"
                                :options="availableFilterOptions.trajectory_types"
                                :placeholder="uLang('app.trajectory_types')"
                                :close-on-select="false"
                                :show-labels="false"
                                @input="updateData()"
                                :limit="3"
                                :limit-text="limitText('trajectory_types')"
                                id="trajectory_types"
                                name="trajectory_types"
                                track-by="id"
                                label="name"
                                return="id"
                                multiple
                            >
                                <template slot="tag" slot-scope="props">
                                    <span class="multiselect__tag" :title="props.option && props.option.name">
                                        <span>{{ props.option && props.option.name }}</span>
                                        <i class="multiselect__tag-icon" aria-hidden="true" @mousedown.prevent="props.remove(props.option)"></i>
                                    </span>
                                </template>
                                <template slot="beforeList">
                                    <li class="multiselect__element multiselect__element__idplein" @click.prevent.stop="handleCustomOptionTrajectoryType()"><span class="multiselect__option"><span>Selecteer alles</span></span></li>
                                </template>
                                <template slot="clear" slot-scope="props">
                                    <div v-show="filters.trajectory_types.length"
                                         @click="clearFilter('trajectory_types', [])"
                                         class="multiselect__clear"
                                    ></div>
                                </template>
                            </multi-select>
                        </div>
                    </div>
                </div>

                <!-- Trajectories and clients -->
                <div class="row form-group">
                    <div class="col-md-6 col-sm-12">
                        <label class="col-md-2 col-sm-12 col-form-label">{{ uLang('app.clients') }}</label>
                        <div class="col-md-9 col-xl-10 col-sm-12">
                            <multi-select
                                v-model="selectedClients"
                                :options="availableFilterOptions.clients"
                                :placeholder="uLang('app.clients')"
                                :close-on-select="false"
                                :show-labels="false"
                                @input="updateData()"
                                :limit="3"
                                :limit-text="limitText('clients')"
                                id="clients"
                                name="clients"
                                track-by="id"
                                label="name"
                                return="id"
                                multiple
                            >
                                <template slot="tag" slot-scope="props">
                                    <span class="multiselect__tag" :title="props.option && props.option.name">
                                        <span>{{ props.option && props.option.name }}</span>
                                        <i class="multiselect__tag-icon" aria-hidden="true" @mousedown.prevent="props.remove(props.option)"></i>
                                    </span>
                                </template>
                                <template slot="beforeList">
                                    <li class="multiselect__element multiselect__element__idplein" @click.prevent.stop="handleCustomOptionClient()"><span class="multiselect__option"><span>Selecteer alles</span></span></li>
                                </template>
                                <template slot="clear" slot-scope="props">
                                    <div v-show="filters.clients.length"
                                         @click="clearFilter('clients', [])"
                                         class="multiselect__clear"
                                    ></div>
                                </template>
                            </multi-select>
                        </div>
                    </div>

                    <div class="col-md-6 col-sm-12">
                        <label class="col-md-2 col-sm-12 col-form-label">{{ uLang('app.trajectories') }}</label>
                        <div class="col-md-9 col-xl-10 col-sm-12">
                            <multi-select
                                v-model="selectedTrajectories"
                                :options="availableFilterOptions.trajectories"
                                :placeholder="uLang('app.trajectories')"
                                :close-on-select="false"
                                :show-labels="false"
                                @input="updateData()"
                                :limit="2"
                                :limit-text="limitText('trajectories')"
                                id="trajectories"
                                name="trajectories"
                                track-by="id"
                                label="name"
                                return="id"
                                multiple
                            >
                                <template slot="tag" slot-scope="props">
                                    <span class="multiselect__tag" :title="props.option && props.option.name">
                                        <span>{{ props.option && props.option.name }}</span>
                                        <i class="multiselect__tag-icon" aria-hidden="true" @mousedown.prevent="props.remove(props.option)"></i>
                                    </span>
                                </template>
                                <template slot="beforeList">
                                    <li class="multiselect__element multiselect__element__idplein" @click.prevent.stop="handleCustomOptionTrajectory()"><span class="multiselect__option"><span>Selecteer alles</span></span></li>
                                </template>
                                <template slot="clear" slot-scope="props">
                                    <div v-show="filters.trajectories.length"
                                         @click="clearFilter('trajectories', [])"
                                         class="multiselect__clear"
                                    ></div>
                                </template>
                            </multi-select>
                        </div>
                    </div>
                </div>

                <!-- Regions and coachServices -->
                <div class="row form-group">
                    <div class="col-md-6 col-sm-12">
                        <label class="col-md-2 col-sm-12 col-form-label">{{ uLang('app.regions') }}</label>
                        <div class="col-md-9 col-xl-10 col-sm-12">
                            <multi-select
                                v-model="selectedRegions"
                                :options="availableFilterOptions.regions"
                                :placeholder="uLang('app.regions')"
                                :close-on-select="false"
                                :show-labels="false"
                                @input="updateData()"
                                :limit="3"
                                :limit-text="limitText('regions')"
                                id="regions"
                                name="regions"
                                track-by="id"
                                label="name"
                                return="id"
                                multiple
                            >
                                <template slot="tag" slot-scope="props">
                                    <span class="multiselect__tag" :title="props.option && props.option.name">
                                        <span>{{ props.option && props.option.name }}</span>
                                        <i class="multiselect__tag-icon" aria-hidden="true" @mousedown.prevent="props.remove(props.option)"></i>
                                    </span>
                                </template>
                                <template slot="beforeList">
                                    <li class="multiselect__element multiselect__element__idplein" @click.prevent.stop="handleCustomOptionRegion()"><span class="multiselect__option"><span>Selecteer alles</span></span></li>
                                </template>
                                <template slot="clear" slot-scope="props">
                                    <div v-show="filters.regions.length"
                                         @click="clearFilter('regions', [])"
                                         class="multiselect__clear"
                                    ></div>
                                </template>
                            </multi-select>
                        </div>
                    </div>

                    <div class="col-md-6 col-sm-12">
                        <label class="col-md-2 col-sm-12 col-form-label">{{ uLang('app.coach_services') }}</label>
                        <div class="col-md-9 col-xl-10 col-sm-12">
                            <multi-select
                                v-model="selectedCoachServices"
                                :options="availableFilterOptions.coach_services"
                                :placeholder="uLang('app.coach_services')"
                                :close-on-select="false"
                                :show-labels="false"
                                @input="updateData()"
                                :limit="3"
                                :limit-text="limitText('coach_services')"
                                id="coach_services"
                                name="coach_services"
                                track-by="id"
                                label="name"
                                return="id"
                                multiple
                            >
                                <template slot="tag" slot-scope="props">
                                    <span class="multiselect__tag" :title="props.option && props.option.name">
                                        <span>{{ props.option && props.option.name }}</span>
                                        <i class="multiselect__tag-icon" aria-hidden="true" @mousedown.prevent="props.remove(props.option)"></i>
                                    </span>
                                </template>
                                <template slot="beforeList">
                                    <li class="multiselect__element multiselect__element__idplein" @click.prevent.stop="handleCustomOptionCoachService()"><span class="multiselect__option"><span>Selecteer alles</span></span></li>
                                </template>
                                <template slot="clear" slot-scope="props">
                                    <div v-show="filters.coach_services.length"
                                         @click="clearFilter('coach_services', [])"
                                         class="multiselect__clear"
                                    ></div>
                                </template>
                            </multi-select>
                        </div>
                    </div>
                </div>
            </template>
        </div>
        <div class="plain-card-2">
            <scrollable-second :loading="loading">
                <template slot="header">
                    <tr>
                        <th class="icon-sort-header" @click="sortTable('full_name')">{{ uLang('app.report_coach') }} ({{ num_all_coach }}) 
                            <span class="icon-sort" :class="{'-ascending': sortColumn === 'full_name' && sortOrder === 'asc', '-descending': sortColumn === 'full_name' && sortOrder === 'desc'}"></span>
                        </th>
                        <th class="icon-sort-header" @click="sortTable('text_region_name')">{{ uLang('app.report_region') }}
                            <span class="icon-sort" :class="{'-ascending': sortColumn === 'text_region_name' && sortOrder === 'asc', '-descending': sortColumn === 'text_region_name' && sortOrder === 'desc'}"></span>
                        </th>
                        <th class="icon-sort-header" @click="sortTable('text_success_trajectory')">{{ uLang('app.report_total_success_trajectory') }}
                            <span class="icon-sort" :class="{'-ascending': sortColumn === 'text_success_trajectory' && sortOrder === 'asc', '-descending': sortColumn === 'text_success_trajectory' && sortOrder === 'desc'}"></span>
                        </th>
                        <th class="icon-sort-header" @click="sortTable('text_positive_result')">{{ uLang('app.report_positive_result') }}
                            <span class="icon-sort" :class="{'-ascending': sortColumn === 'text_positive_result' && sortOrder === 'asc', '-descending': sortColumn === 'text_positive_result' && sortOrder === 'desc'}"></span>
                        </th>
                        <th class="icon-sort-header" @click="sortTable('text_average_rating')">{{ uLang('app.report_customer_satisfaction') }}
                            <div class="wrapper-activity-popover">
                                <i id="popover-activity-name-trigger" class="fa fa-exclamation-circle" 
                                @mouseenter="showPopover" 
                                @mouseleave="hidePopover"></i>
                                <div id="popover-activity-name-content" class="activity-popover"
                                    v-show="stateShow"
                                    @mouseenter="stateShow = true" 
                                    @mouseleave="stateShow = false"
                                >Deze scores worden getoond op basis van de gefilterde periode</div>
                            </div>
                            <span class="icon-sort" :class="{'-ascending': sortColumn === 'text_average_rating' && sortOrder === 'asc', '-descending': sortColumn === 'text_average_rating' && sortOrder === 'desc'}"></span>
                        </th>
                        <th class="icon-sort-header" @click="sortTable('text_own_recruitment')">{{ uLang('app.report_own_recruitment') }}
                            <span class="icon-sort" :class="{'-ascending': sortColumn === 'text_own_recruitment' && sortOrder === 'asc', '-descending': sortColumn === 'text_own_recruitment' && sortOrder === 'desc'}"></span>
                        </th>
                        <!-- REDO ID-325 <th v-if="authUser.type_id === 5" class="icon-sort-header" @click="sortTable('time_spent')">{{ uLang('app.time_spent') }}
                            <span class="icon-sort" :class="{'-ascending': sortColumn === 'time_spent' && sortOrder === 'asc', '-descending': sortColumn === 'time_spent' && sortOrder === 'desc'}"></span>
                        </th> -->
                        <th class="icon-sort-header" @click="sortTable('turnover')">{{ uLang('app.report_turnover') }}
                            <span class="icon-sort" :class="{'-ascending': sortColumn === 'turnover' && sortOrder === 'asc', '-descending': sortColumn === 'turnover' && sortOrder === 'desc'}"></span>
                        </th>
                        <th v-if="authUser.type_id === 5" class="icon-sort-header" @click="sortTable('sales_turnover')">{{ uLang('app.report_sales_turnover') }}
                            <span class="icon-sort" :class="{'-ascending': sortColumn === 'sales_turnover' && sortOrder === 'asc', '-descending': sortColumn === 'sales_turnover' && sortOrder === 'desc'}"></span>
                        </th>
                    </tr>
                    <tr class="summary-1">
                        <th>{{ uLang('app.average') }}</th>
                        <th></th>
                        <th>{{ Intl.NumberFormat('de-DE', {
                            style: 'decimal',
                            minimumFractionDigits: 1,
                            maximumFractionDigits: 1
                        }).format(average_success_trajectory) }}</th>
                        <th>{{average_positive_result.toFixed(0)}}%</th>
                        <th>{{ Intl.NumberFormat('de-DE', {
                            style: 'decimal',
                            minimumFractionDigits: 1,
                            maximumFractionDigits: 1
                        }).format(average_customer_satisfaction) }}</th>
                        <th>{{average_own_recruitment.toFixed(0)}}%</th>
                        <!-- REDO ID-325 <th v-if="authUser.type_id === 5">{{ average_time_spent }}</th> -->
                        <th>€{{ Intl.NumberFormat('de-DE', {
                            style: 'decimal',
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0
                        }).format(average_turnover) }}<sup class="text-muted turnover-note">*</sup></th>
                        <th v-if="authUser.type_id === 5">€{{ Intl.NumberFormat('de-DE', {
                            style: 'decimal',
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0
                        }).format(average_sales_turnover) }}<sup class="text-muted turnover-note">*</sup></th>
                    </tr>
                    <tr class="summary-2">
                        <th>{{ uLang('app.total') }}</th>
                        <th></th>
                        <th>{{ Intl.NumberFormat('de-DE', {
                                style: 'decimal',
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 2
                            }).format(total_success_trajectory) }}</th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <!-- REDO ID-325 <th v-if="authUser.type_id === 5">{{ total_time_spent }}</th> -->
                        <th>€{{ Intl.NumberFormat('de-DE', {
                            style: 'decimal',
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0
                        }).format(total_turnover) }}<sup class="text-muted turnover-note">*</sup></th>
                        <th v-if="authUser.type_id === 5">€{{ Intl.NumberFormat('de-DE', {
                            style: 'decimal',
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0
                        }).format(total_sales_turnover) }}<sup class="text-muted turnover-note">*</sup></th>
                    </tr>
                </template>
                <tbody slot="body">
                    <tr v-for="coach in coachs" :key="coach.id">
                        <td>
                            <template v-if="authUser.type_id === 5 || authUser.id === coach.id">
                                <a class="coach-name-link" :href="'/admin/relations/'+ coach.id" target="_blank">{{ coach.full_name }}</a>
                            </template>
                            <template v-else>
                                {{ coach.full_name }}
                            </template>
                        </td>
                        <td>{{ coach.text_region_name }}</td>
                        <td>
                            <template v-if="authUser.type_id === 5 || authUser.id === coach.id">
                                <a href="#" @click.prevent="loadModalSuccessTrajectory(coach)" target="_blank">{{ coach.text_success_trajectory }}</a>
                            </template>
                            <template v-else>
                                {{ coach.text_success_trajectory }}
                            </template>
                        </td>
                        <td>
                            <template v-if="authUser.type_id === 5 || authUser.id === coach.id">
                                <a href="#" @click.prevent="loadModalPositiveResult(coach)" target="_blank">{{ getTextInPercent(coach.text_positive_result) }}</a>
                            </template>
                            <template v-else>
                                {{ getTextInPercent(coach.text_positive_result) }}
                            </template>
                        </td>
                        <td>
                            <template v-if="authUser.type_id === 5 || authUser.id === coach.id">
                                <a :href="'/admin/reports/coach-feedback/'+ coach.id" target="_blank">
                                    {{ 
                                        Intl.NumberFormat('de-DE', {
                                            style: 'decimal',
                                            minimumFractionDigits: 0,
                                            maximumFractionDigits: 2
                                        }).format(getAverageRating(coach.text_average_rating))
                                    }}</a>
                            </template>
                            <template v-else>
                                {{ Intl.NumberFormat('de-DE', {
                                    style: 'decimal',
                                    minimumFractionDigits: 0,
                                    maximumFractionDigits: 2
                                }).format(getAverageRating(coach.text_average_rating)) }}
                            </template>
                        </td>
                        <td>
                            <template v-if="authUser.type_id === 5 || authUser.id === coach.id">
                                <a href="#" @click.prevent="loadModalOwnRecruitment(coach)" target="_blank">{{ getTextInPercent(coach.text_own_recruitment) }}</a>
                            </template>
                            <template v-else>
                                {{ getTextInPercent(coach.text_own_recruitment) }}
                            </template>
                        </td>
                        <!-- REDO ID-325 <td v-if="authUser.type_id === 5">{{ coach.time_spent }}</td> -->
                        <td>
                            <template v-if="authUser.type_id === 5 || authUser.id === coach.id">
                                <a href="#" @click.prevent="loadModalTurnOver(coach)" target="_blank">
                                €{{ Intl.NumberFormat('de-DE', {
                                style: 'decimal',
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 0
                                }).format(coach.turnover) }}
                                </a>
                            </template>
                            <template v-else>
                                €{{ Intl.NumberFormat('de-DE', {
                                style: 'decimal',
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 0
                                }).format(coach.turnover) }}
                            </template>
                        </td>
                        <td v-if="authUser.type_id === 5"><a href="#" @click.prevent="loadModalSaleTurnOver(coach)" target="_blank">€{{ Intl.NumberFormat('de-DE', {
                            style: 'decimal',
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0
                        }).format(coach.sales_turnover) }}</a></td>
                    </tr>
                </tbody>
            </scrollable-second>

            <div v-if="reportMeta && reportMeta.last_page > 1" class="VuePagination mt-2 mb-2 px-2">
                <ul class="pagination VuePagination__pagination mb-0">
                    <li class="VuePagination__pagination-item page-item VuePagination__pagination-item-prev-page"
                        :class="{ disabled: reportMeta.current_page <= 1 }">
                        <a class="page-link" href="javascript:void(0);" @click="goToPage(reportMeta.current_page - 1)">&lt;</a>
                    </li>
                    <li v-for="p in reportPaginationPages" :key="p"
                        class="VuePagination__pagination-item page-item"
                        :class="{ active: reportMeta.current_page === p }">
                        <a class="page-link" href="javascript:void(0);" @click="goToPage(p)">{{ p }}</a>
                    </li>
                    <li class="VuePagination__pagination-item page-item VuePagination__pagination-item-next-page"
                        :class="{ disabled: reportMeta.current_page >= reportMeta.last_page }">
                        <a class="page-link" href="javascript:void(0);" @click="goToPage(reportMeta.current_page + 1)">&gt;</a>
                    </li>
                </ul>
                <p class="VuePagination__count text-muted mb-0 mt-1">{{ reportPaginationCount }}</p>
            </div>

            <div class="turnover-footnote text-muted small mt-3">
                <p class="mb-1"><strong>*</strong> Toelichting bij <em>Omzet</em> en <em>Verkoopomzet</em>:</p>
                <ul class="mb-0 pl-3">
                    <li>Bedragen zijn <strong>exclusief btw</strong>.</li>
                    <li>Alleen facturen die in <strong>Twinfield zijn geboekt</strong> (verzonden) worden meegeteld.</li>
                    <li><em>Omzet</em> en <em>Verkoopomzet</em> tellen ook coaches mee waarvan de <strong>samenwerking is beëindigd</strong>, zolang zij facturen binnen de geselecteerde periode hebben.</li>
                    <li><em>Omzet</em> en <em>Verkoopomzet</em> tellen <strong>alle trajecten</strong> mee, ook trajecten die <strong>niet</strong> in het resultatendashboard worden getoond.</li>
                    <li>De totalen kunnen <strong>licht afwijken van de som van de zichtbare rijen</strong>: de totalen worden berekend over alle geboekte facturen in de periode (boekhoudkundig totaal), terwijl de rijen per getoonde coach worden weergegeven.</li>
                    <li>Een klein restverschil met de boekhouding kan overblijven door facturen die <strong>rechtstreeks in Twinfield</strong> zijn geboekt, verwijderde facturen of verschillen rond de <strong>periodegrenzen</strong>.</li>
                </ul>
            </div>

            <div v-if="isShowingModal" class="modal fade show d-block" tabindex="-1" role="dialog" aria-labelledby="modalLabel" aria-hidden="true" style="background: rgba(0, 0, 0, 0.5);">
                <div class="modal-dialog modal-dialog-scrollable modal-lg" role="document">
                    <div class="modal-content">
                        <div class="modal-header">
                            <h5 class="modal-title" id="modalLabel">{{ uLang('app.overview_trajectory_enrolments') }}</h5>
                        </div>
                        <div class="modal-body" style="max-height: 500px; overflow-y: auto;">
                            <div class="row">
                                <div class="col-12 enrolment" v-if="enrolments.length > 0" v-for="enrolment in enrolments" :key="enrolment.id">
                                    <router-link :to="{
                                            name: 'admin.relations.trajectory-enrolments.view',
                                            params: {
                                                'relationId': enrolment.candidate_id,
                                                'trajectoryEnrolmentId': enrolment.id,
                                            }
                                        }"
                                    >
                                        <h5 style="color: black;">{{ $getIfExists(enrolment, 'trajectory_name') }}</h5>
                                    </router-link>

                                    <span class="enrolment-info" :title="uLang('app.candidate')">
                                        <i class="fa fa-lg fa-user"></i>
                                        <link-if-permissions
                                            :to="{ name: 'admin.relations.view', params: { id: enrolment.candidate_id }}"
                                            permissions="view-relations"
                                        >
                                            <span class="text-brand">
                                                {{ getCandidateName(enrolment) }}
                                            </span>
                                        </link-if-permissions>
                                    </span>
                                </div>
                                <div class="col-12" v-if="!enrolments || enrolments.length === 0">
                                    {{ uLang('app.no_enrolments') }}
                                </div>
                            </div>
                        </div>
                        <div class="modal-footer">
                            <button type="button" class="btn btn-primary" @click="isShowingModal = false">{{ uLang('app.close') }}</button>
                        </div>
                    </div>
                </div>
            </div>


        </div>
    </div>
</template>

<script>
    import MultiSelect from 'vue-multiselect';
    import { TrajectoryManagerSelect } from '~/Form/elements';
    import { ReaLoader } from "~/components/helpers";
    import { mapGetters } from 'vuex'
    import ManagementInfoResultRow from "~/components/tables/table-rows/ManagementInfoResultRow"
    import { ToggleFiltersMixin } from '~/mixins/management-info'
    import SettingSelect from "../../../Form/elements/setting-select"
    import { debounce } from 'lodash'
    import ScrollableSecond from '~/components/tables/templates/scrollablesecond'

    export default {
        name: 'results_new',
        components: {
            SettingSelect,
            ManagementInfoResultRow,
            ReaLoader,
            MultiSelect,
            TrajectoryManagerSelect,
            ScrollableSecond
        },
        mixins: [ToggleFiltersMixin],
        watch: {
            sortColumn() {
                if (!this.reportMeta || this.coachs.length === 0) return;
                this.sortData();
            },
            sortOrder() {
                if (!this.reportMeta || this.coachs.length === 0) return;
                this.sortData();
            },
        },
        data() {
            return {
                show_filters: true,
                loading: true,
                loadingFilters: false,
                coachs: [],
                selected_date_preset: null,
                date_filter_presets: {
                    all: {
                        months: null,
                        name: this.uLang('app.all_results')
                    },
                    three: {
                        months: 3,
                        name: `${ this.uLang('app.current_calendar_year')}`,
                    },
                    six: {
                        months: 6,
                        name: `${ this.uLang('app.previous_calendar_year')}`,
                    },
                    twelve: {
                        months: 12,
                        name: `12 ${ this.uLang('app.months') }`,
                    },
                    custom: {
                        months: null,
                        name: this.uLang('app.custom_input'),
                    },
                },
                filters: {
                    date_from: null,
                    date_to: null,
                    trajectory_types: [],
                    projects: [],
                    trajectories: [],
                    clients: [],
                    regions: [],
                    collaboration_statuses: {
                        active: true,
                        ended: false
                    },
                    coach_services: [],
                    page: 1,
                    per_page: 30,
                    sort_column: 'text_region_name',
                    sort_order: 'asc',
                },
                reportMeta: null,
                availableFilterOptions: {
                    trajectory_types: [],
                    projects: [],
                    trajectories: [],
                    clients: [],
                    regions: [],
                    coach_services: [],
                },
                num_all_coach: 0,

                total_success_trajectory: 0,
                total_turnover: 0,
                total_sales_turnover: 0,
                average_success_trajectory: 0,
                average_turnover: 0,
                average_sales_turnover: 0,

                // REDO ID-325 total_time_spent: 0,
                // REDO ID-325 average_time_spent: 0,

                average_positive_result: 0,
                average_customer_satisfaction: 0,
                average_own_recruitment: 0,

                sortColumn: 'text_region_name',
                sortOrder: 'asc',
                // Keep in sync with filters for API

                isShowingModal: false,
                enrolments: [],

                stateShow: false
            }
        },
        async created() {
            this.getTrajectoryTypes()
            this.defaultFilters()
        },
        computed: {
            ...mapGetters(['authUser', 'settingByName']),
            selectedTrajectoryTypes: {
                get() {
                    return this.selectedEntityGetter('trajectory_types')
                },
                set(trajectory_types) {
                    this.selectedEntitySetter(trajectory_types, 'trajectory_types')
                }
            },
            selectedProjects: {
                get() {
                    return this.selectedEntityGetter('projects')
                },
                set(projects) {
                    this.selectedEntitySetter(projects, 'projects')
                }
            },
            selectedTrajectories: {
                get() {
                    return this.selectedEntityGetter('trajectories')
                },
                set(trajectories) {
                    this.selectedEntitySetter(trajectories, 'trajectories')
                }
            },
            selectedClients: {
                get() {
                    return this.selectedEntityGetter('clients')
                },
                set(clients) {
                    this.selectedEntitySetter(clients, 'clients')
                }
            },
            selectedRegions: {
                get() {
                    return this.selectedEntityGetter('regions')
                },
                set(regions) {
                    this.selectedEntitySetter(regions, 'regions')
                }
            },
            selectedCoachServices: {
                get() {
                    return this.selectedEntityGetter('coach_services')
                },
                set(coach_services) {
                    this.selectedEntitySetter(coach_services, 'coach_services')
                }
            },
            reportPaginationPages() {
                if (!this.reportMeta || this.reportMeta.last_page <= 1) return []
                const current = this.reportMeta.current_page
                const last = this.reportMeta.last_page
                const half = 3
                let start = Math.max(1, current - half)
                let end = Math.min(last, current + half)
                if (end - start < 2 * half) {
                    if (start === 1) end = Math.min(last, start + 2 * half)
                    else end = Math.min(last, current + half)
                }
                return Array.from({ length: end - start + 1 }, (_, i) => start + i)
            },
            reportPaginationCount() {
                if (!this.reportMeta || this.reportMeta.total === 0) return ''
                const total = this.reportMeta.total
                const per = this.reportMeta.per_page
                const page = this.reportMeta.current_page
                const start = (page - 1) * per + 1
                const end = Math.min(page * per, total)
                if (this.reportMeta.last_page <= 1) return `${total} resultaten`
                return `${start} tot ${end} van de ${total} resultaten`
            },
        },
        methods: {
            handleCustomOptionProject() {
                this.filters.projects = this.availableFilterOptions.projects.map(p => p.id);
                this.$refs.multiSelectProject.deactivate();
                this.updateData();
            },
            handleCustomOptionTrajectory() {
                this.filters.trajectories = this.availableFilterOptions.trajectories.map(p => p.id);
                this.$refs.multiSelectTrajectory.deactivate();
                this.updateData();
            },
            handleCustomOptionClient() {
                this.filters.clients = this.availableFilterOptions.clients.map(p => p.id);
                this.$refs.multiSelectClient.deactivate();
                this.updateData();
            },
            handleCustomOptionRegion() {
                this.filters.regions = this.availableFilterOptions.regions.map(p => p.id);
                this.$refs.multiSelectRegion.deactivate();
                this.updateData();
            },
            handleCustomOptionTrajectoryType() {
                this.filters.trajectory_types = this.availableFilterOptions.trajectory_types.map(p => p.id);
                this.$refs.multiSelectTrajectoryType.deactivate();
                this.updateData();
            },
            handleCustomOptionCoachService() {
                this.filters.coach_services = this.availableFilterOptions.coach_services.map(p => p.id);
                this.$refs.multiSelectCoachService.deactivate();
                this.updateData();
            },
            showPopover() {
                this.stateShow = true;
            },
            hidePopover() {
                this.stateShow = false;
            },
            getCandidateName(enrolment) {
                const first_letters = $getIfExists(enrolment, 'candidate_first_letters')
                const first_name = $getIfExists(enrolment, 'candidate_first_name')
                const prefix = $getIfExists(enrolment, 'candidate_prefix')
                const last_name = $getIfExists(enrolment, 'candidate_last_name')

                return `${ (first_name || first_letters || '') } ${ (prefix || '') } ${ last_name }`
            },
            loadModalSuccessTrajectory(coach) {
                this.isShowingModal = true;
                this.enrolments = coach.management_info_results.filter(item => 
                    [4, 5, 6, 7].includes(item.status_id)
                );
            },
            loadModalPositiveResult(coach) {
                this.isShowingModal = true;
                this.enrolments = coach.management_info_results.filter(item => 
                    [4, 5].includes(item.status_id)
                );
            },
            loadModalOwnRecruitment(coach) {
                this.isShowingModal = true;
                this.enrolments = coach.my_own_recruitment_management_info_result.filter(item => 
                    item.coach_id === item.recieves_lead_person_id
                );
            },
            loadModalTurnOver(coach) {
                this.isShowingModal = true;
                this.enrolments = coach.management_info_results_all;
            },
            loadModalSaleTurnOver(coach) {
                this.isShowingModal = true;
                this.enrolments = coach.management_info_results_all;
            },
            sortData() {
                this.coachs = [...this.coachs].sort((a, b) => {
                    const valueA = a[this.sortColumn];
                    const valueB = b[this.sortColumn];

                    if (typeof valueA === 'string' && typeof valueB === 'string') {
                    return this.sortOrder === 'asc'
                        ? valueA.localeCompare(valueB)
                        : valueB.localeCompare(valueA);
                    }

                    if (valueA < valueB) return this.sortOrder === 'asc' ? -1 : 1;
                    if (valueA > valueB) return this.sortOrder === 'asc' ? 1 : -1;
                    return 0;
                });
            },
            filteredTotalSuccessTrajectoryCount(managementInfo) {
                return managementInfo.filter(item => 
                    [4, 5, 6, 7].includes(item.status_id)
                ).length;
            },
            filteredCompletedPlusSuccessTrajectoryCount(managementInfo) {
                return managementInfo.filter(item => 
                    [4, 5].includes(item.status_id)
                ).length;
            },
            getTextInPercent(number) {
                return number.toFixed(0) + "%";
            },
            getCompletedPlusSuccessTrajectoryNumber(managementInfo) {
                const completedPlusSuccess = this.filteredCompletedPlusSuccessTrajectoryCount(managementInfo);
                const totalSuccessTrajectory = this.filteredTotalSuccessTrajectoryCount(managementInfo);
                if (totalSuccessTrajectory === 0) {
                    return 0;
                }
                const percentage = (completedPlusSuccess / totalSuccessTrajectory) * 100;
                return percentage;
            },
            filteredSuccessTrajectoryCount(managementInfo) {
                return managementInfo.filter(item => 
                    [5].includes(item.status_id)
                ).length;
            },
            getOwnRecruitmentNumber(managementInfo) {
                const ownRecruitment = managementInfo.filter(item => 
                    item.coach_id === item.recieves_lead_person_id
                ).length;
                const total = managementInfo.length;
                if (total === 0) {
                    return 0;
                }
                const percentage = (ownRecruitment / total) * 100;
                return percentage;
            },
            getAverageRating(number) {
                return Math.round(number * 100) / 100;
            },
            defaultFilters() {
                if (localStorage.getItem("filtersSavedSelectedDatePresetNew"))
                {
                    const preset = JSON.parse(localStorage.getItem("filtersSavedSelectedDatePresetNew"));
                    this.applyDatePreset(this.date_filter_presets[preset]?.months, preset);
                }
                else
                {
                    this.applyDatePreset(3, 'three')
                }
                if (localStorage.getItem("filtersSavedNew"))
                {
                    this.filters = JSON.parse(localStorage.getItem("filtersSavedNew"));

                    if(this.filters.date_from === "1970-01-01T00:00:00.000Z") {
                        this.filters.date_from = null;
                    } else {
                        this.filters.date_from = new Date(this.filters.date_from);
                    }
                    if(this.filters.date_to === "1970-01-01T00:00:00.000Z") {
                        this.filters.date_to = null;
                    } else {
                        this.filters.date_to = new Date(this.filters.date_to);
                    }
                    if (this.filters.page == null) this.filters.page = 1;
                    if (this.filters.per_page == null) this.filters.per_page = 30;
                    if (this.filters.sort_column == null) this.filters.sort_column = 'text_region_name';
                    if (this.filters.sort_order == null) this.filters.sort_order = 'asc';
                    this.sortColumn = this.filters.sort_column;
                    this.sortOrder = this.filters.sort_order;
                }
                this.updateData(true)
            },
            limitText: (entity) => count => uLang('app.multiselect_overflow', {
                count,
                entity: lang(`app.${ entity }`).toLowerCase()
            }),
            selectedEntityGetter(entityName) {
                return this.filters[entityName].map(entityId => this.availableFilterOptions[entityName].find(entity => entity.id === entityId))
            },
            selectedEntitySetter(entity, entityName) {
                this.filters[entityName] = entity.map(entity => entity.id)
            },
            applyDatePreset(months, key) {
                this.selected_date_preset = key

                if (key === 'all') {
                    this.filters.date_from = null
                    this.filters.date_to = null
                }

                if (months === 3 || months === 6) {
                    if(months === 3 ) {
                        this.filters.date_from = moment().startOf('year').toDate();
                        this.filters.date_to = moment().endOf('year').toDate();
                    } else if(months === 6) {
                        this.filters.date_from = moment().subtract(1, 'year').startOf('year').toDate();
                        this.filters.date_to = moment().subtract(1, 'year').endOf('year').toDate();
                    }
                } else {
                    this.filters.date_from = months ? moment().subtract(months, 'months').toDate() : null;
                    this.filters.date_to = months ? moment().toDate() : null;
                }
                this.updateData()
            },
            filterCollaborationStatus(status) {
                this.filters.collaboration_statuses[status] = !this.filters.collaboration_statuses[status]
                this.updateData()
            },
            clearFilter(name, value = null) {
                this.filters[name] = value
                this.updateData()
            },
            async fetchData() {
                this.loading = true;

                if(this.filters.date_from === "1970-01-01T00:00:00.000Z" || this.filters.date_from?.toString() === (new Date(0)).toString()) {
                    this.filters.date_from = null;
                }
                if(this.filters.date_to === "1970-01-01T00:00:00.000Z" || this.filters.date_to?.toString() === (new Date(0)).toString()) {
                    this.filters.date_to = null;
                }

                let filters = JSON.stringify({
                    ...this.filters,
                    date_from: this.filters.date_from ? this.filters.date_from?.toDateString() ?? null : null,
                    date_to: this.filters.date_to ? this.filters.date_to?.toDateString() ?? null : null,
                })

                // Call list and totals in parallel so the page feels faster
                const [listRes, totalsRes] = await Promise.all([
                    window.axios.get(route('management-info.results-new', { filters })),
                    window.axios.get(route('management-info.results-new-totals', { filters })).catch(() => ({ data: {} }))
                ])
                const data = listRes.data
                const list = Array.isArray(data) ? data : (data.data ?? [])
                this.reportMeta = data.meta ?? null
                if (this.reportMeta && totalsRes.data && totalsRes.data.totals) {
                    this.reportMeta.totals = totalsRes.data.totals
                }
                this.coachs = list

                this.coachs.map(coach => {
                    coach.text_region_name = coach.text_region_name ?? coach.region_name ?? '-';
                    coach.text_success_trajectory = coach.text_success_trajectory ?? this.filteredTotalSuccessTrajectoryCount(coach.management_info_results);
                    coach.text_positive_result = coach.text_positive_result ?? this.getCompletedPlusSuccessTrajectoryNumber(coach.management_info_results);
                    coach.text_average_rating = coach.text_average_rating ?? coach.my_average_rating ?? 0;
                    coach.text_own_recruitment = coach.text_own_recruitment ?? this.getOwnRecruitmentNumber(coach.my_own_recruitment_management_info_result);
                    return coach;
                })

                this.getAllStatistic(this.coachs);
                this.filterOnlyHasSuccessTrajectory();
                if (this.reportMeta) {
                    this.num_all_coach = this.reportMeta.total;
                }
                this.loading = false;
            },
            async fetchGlobalTotals(filters) {
                try {
                    const { data } = await window.axios.get(route('management-info.results-new-totals', { filters }))
                    if (data.totals && this.reportMeta) {
                        this.reportMeta.totals = data.totals
                        this.getAllStatistic(this.coachs)
                    }
                } catch (_) {
                    // Totals optional; use meta.totals from results-new when available
                }
            },
            goToPage(page) {
                if (page < 1 || (this.reportMeta && page > this.reportMeta.last_page)) return
                this.filters.page = page
                this.updateData(true)
            },
            filterOnlyHasSuccessTrajectory() {
                if (this.filters.projects.length > 0 || this.filters.clients.length > 0) {
                    this.coachs = this.coachs.filter(coach => {
                        return coach.text_success_trajectory > 0;
                    });
                }
            },
            getAllStatistic(coachs) {
                if (this.reportMeta && this.reportMeta.totals) {
                    const t = this.reportMeta.totals;
                    this.num_all_coach = this.reportMeta.total;
                    this.total_success_trajectory = t.total_success_trajectory ?? 0;
                    this.total_turnover = t.total_turnover ?? 0;
                    this.total_sales_turnover = t.total_sales_turnover ?? 0;
                    this.average_success_trajectory = t.average_success_trajectory ?? 0;
                    this.average_turnover = t.average_turnover ?? 0;
                    this.average_sales_turnover = t.average_sales_turnover ?? 0;
                    this.average_positive_result = t.average_positive_result ?? 0;
                    this.average_customer_satisfaction = t.average_customer_satisfaction ?? 0;
                    this.average_own_recruitment = t.average_own_recruitment ?? 0;
                    return;
                }

                let sum_success_trajectory = 0;
                let sum_turnover = 0;
                let sum_sales_turnover = 0;

                let sum_positive_result = 0;
                let sum_customer_satisfaction = 0;
                let sum_own_recruitment = 0;

                let num_all_coach = coachs.length;

                coachs.forEach(coach => {
                    sum_success_trajectory += coach.text_success_trajectory;
                    sum_turnover += coach.turnover;
                    sum_sales_turnover += coach.sales_turnover;

                    sum_positive_result += coach.text_positive_result;
                    sum_customer_satisfaction += coach.text_average_rating;
                    sum_own_recruitment += coach.text_own_recruitment;
                });

                this.total_success_trajectory = sum_success_trajectory;
                this.num_all_coach = num_all_coach;
                this.total_turnover = sum_turnover;
                this.total_sales_turnover = sum_sales_turnover;

                if (this.num_all_coach !== 0) {
                    this.average_success_trajectory = (sum_success_trajectory / num_all_coach).toFixed(1);
                    this.average_turnover = sum_turnover / num_all_coach;
                    this.average_sales_turnover = sum_sales_turnover / num_all_coach;
                    this.average_positive_result = sum_positive_result / num_all_coach;
                    this.average_customer_satisfaction = ((sum_customer_satisfaction / num_all_coach) * 100) / 100;
                    this.average_own_recruitment = sum_own_recruitment / num_all_coach;

                    // REDO ID-325 this.average_time_spent = sum_time_spent / num_all_coach;
                }
            },

            sortTable(column) {
                if (this.sortColumn === column) {
                    this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
                } else {
                    this.sortColumn = column;
                    this.sortOrder = 'asc';
                }
                this.filters.sort_column = this.sortColumn;
                this.filters.sort_order = this.sortOrder;
                this.updateData(true);
            },

            async getTrajectoryTypes() {
                const { data } = await window.axios.get(route('settings.values.index', { setting: 12 }))

                data.push({ id: null, value: this.uLang('app.trajectories_without_type'), system_name: null })

                const trajectory_type = data.reduce((result, type) => {
                    this.availableFilterOptions.trajectory_types.push({
                        id: type.id,
                        name: type.value,
                        system_name: type.system_name
                    })

                    if (type.system_name !== 'quotation_phase') {
                        result.push(type.id);
                    }

                    return result;
                }, []);

                if (!localStorage.getItem("filtersSavedNew")) {
                    this.filters.trajectory_types = trajectory_type;
                }
            },
            updateData: debounce(function (keepPage) {
                this.recalculateData(keepPage)
            }, 300),

            async recalculateData(keepPage) {
                if (!keepPage) {
                    this.filters.page = 1
                }
                this.loadingFilters = true

                if(this.filters.date_from === "1970-01-01T00:00:00.000Z" || this.filters.date_from?.toString() === (new Date(0)).toString()) {
                    this.filters.date_from = null;
                }
                if(this.filters.date_to === "1970-01-01T00:00:00.000Z" || this.filters.date_to?.toString() === (new Date(0)).toString()) {
                    this.filters.date_to = null;
                }
                
                // Prepare filters
                let filters = JSON.stringify({
                    ...this.filters,
                    date_from: this.filters.date_from ? this.filters.date_from?.toDateString() ?? null : null,
                    date_to: this.filters.date_to ? this.filters.date_to?.toDateString() ?? null : null,
                })

                const { data } = await window.axios.get(route('management-info.result-filters', { filters }))
                
                let { projects, trajectories, clients, regions, coach_services } = data;

                // Clear unavailable options from filters
                this.filters.projects = this.filters.projects.filter((el) => projects.map(p => p.id).includes(el));
                this.filters.trajectories = this.filters.trajectories.filter((el) => trajectories.map(p => p.id).includes(el));
                this.filters.clients = this.filters.clients.filter((el) => clients.map(p => p.id).includes(el));
                this.filters.regions = this.filters.regions.filter((el) => regions.map(p => p.id).includes(el));

                this.filters.coach_services = this.filters.coach_services ?? [];
                this.filters.coach_services = this.filters.coach_services.filter((el) => coach_services.map(p => p.id).includes(el));

                // Set available filter options
                this.availableFilterOptions.projects = projects
                this.availableFilterOptions.trajectories = trajectories
                this.availableFilterOptions.clients = clients
                this.availableFilterOptions.regions = regions
                this.availableFilterOptions.coach_services = coach_services

                this.loadingFilters = false

                localStorage.setItem("filtersSavedNew", JSON.stringify(this.filters));
                localStorage.setItem("filtersSavedSelectedDatePresetNew", JSON.stringify(this.selected_date_preset));
                await this.fetchData()
            },
        }
    }
</script>

<style lang="scss" scoped>
    .multiselect__element__idplein{
        background-color: #ffe9f6;
    }
    .multiselect__element__idplein:hover{
        color:white;
        background-color: #c1006d;
        font-weight: bold;
    }

    ::v-deep .multiselect__content-wrapper {
        width: max-content;
        min-width: 100%;
        max-width: 720px;
        overflow-x: auto;
    }

    ::v-deep .multiselect__content {
        min-width: 100%;
        width: max-content;
    }

    ::v-deep .multiselect__option {
        white-space: nowrap;
    }

    ::v-deep .multiselect__option::after {
        display: none;
        content: none;
    }

    .plain-card ::v-deep .multiselect__tags {
        height: 35px !important;
        overflow: hidden;
        line-height: 35px;
    }

    .plain-card ::v-deep .multiselect__tag {
        max-width: 160px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        vertical-align: middle;
    }

    .plain-card ::v-deep .multiselect__tag > span {
        display: inline-block;
        max-width: 132px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        vertical-align: middle;
    }

    .wrapper-activity-popover {
        position: relative;
        display: inline-block;
    }

    .activity-popover {
        position: absolute;
        background-color: #fff;
        border: 1px solid #ccc;
        padding: 10px;
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
        z-index: 1000;
        top: 20px;
        text-align: left;
        width: 300px;
    }

    .pressed {
        background-color: #c1006d;
        font-weight: bold;
        text-decoration: underline;
    }
    .datePresetButton {
        border: 1px solid white;
    }
    .fa-caret-down {
        transition: transform .3s ease;

        &.active {
            -webkit-transform: rotate(-180deg);
            -moz-transform: rotate(-180deg);
            -o-transform: rotate(-180deg);
            -ms-transform: rotate(-180deg);
            transform: rotate(-180deg);
        }
    }

    .plain-card {
        background-color: white;
        padding: 20px;
        position: relative;
        box-shadow: 3px 1px 5px #ccc;
        &:not(:first-child) {
            margin-top: 30px;
        }
        h2 {
            margin-bottom: 20px;
        }
        table {
            width: 100%;
            td, th {
                padding: 6px;
                text-align: left;
            }
            .divider {
                width: 1%;
                border: none;
            }
            .result {
                width: 6%;
            }
        }
    }

    .plain-card-2 {
        background-color: white;
        padding: 20px;
        position: relative;
        box-shadow: 3px 1px 5px #ccc;
        table {
            th.icon-sort-header {
                padding-right: 14px;
                cursor: pointer;
            }
            th:nth-child(1), th:nth-child(2) {
                text-align: left;
            }
            th:nth-child(3), th:nth-child(4), th:nth-child(5), th:nth-child(6) {
                text-align: center;
            }
            th:nth-child(7), th:nth-child(8) {
                text-align: right;
            }
            tbody {
                td:nth-child(1), td:nth-child(2) {
                    text-align: left;
                }
                td:nth-child(3), td:nth-child(4), td:nth-child(5), td:nth-child(6) {
                    text-align: center;
                }
                td:nth-child(7), td:nth-child(8) {
                    text-align: right;
                }
            }
        }
    }
    .multiselect__clear {
        position: absolute;
        right: 41px;
        height: 40px;
        width: 40px;
        display: block;
        cursor: pointer;
        z-index: 2;
    }
    .multiselect__clear::before {
        transform: rotate(45deg);
    }
    .multiselect__clear::after {
        transform: rotate(-45deg);
    }
    .multiselect__clear::after, .multiselect__clear::before {
        content: "";
        display: block;
        position: absolute;
        width: 3px;
        height: 16px;
        background: #aaa;
        top: 12px;
        right: 4px;
    }




    .table__heading {
        height: auto !important;
        z-index: 10;
        position: relative;
        background-color: white;
    }
    
    .icon-sort {
        position: relative;
        margin-left: 3px;
    }

    .icon-sort::before, .icon-sort::after {
    content: "";
        position: absolute;
        top: 0;
        left: 0;
        width: 0; 
        height: 0;
        border-left: 5px solid transparent;
        border-right: 5px solid transparent;
    }

    .icon-sort::before {
        top: 20%;
        border-bottom: 5px solid #808080;
    }

    .icon-sort::after {
        top: 60%;
        border-top: 5px solid #808080;
    }

    .icon-sort.-ascending::before {
        border-bottom: 5px solid #c1006d;
    }

    .icon-sort.-descending::after {
        border-top: 5px solid #c1006d;
    }

    .coach-name-link {
        color: #c1006d;
        font-weight: bold;
    }
    .coach-name-link:hover {
        color: #a0005a;
        text-decoration: underline;
    }

    .summary-1 th,
    .summary-2 th {
        font-weight: 600;
    }

    .turnover-note {
        margin-left: 2px;
        font-weight: 400;
        color: #999;
    }

    .turnover-footnote {
        font-size: 0.8rem;
        line-height: 1.4;
        color: #888;

        ul {
            list-style: disc;
        }
        li {
            margin-bottom: 2px;
        }
    }
</style>
